import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api, schemesV2Utils } from "../../services/schemesV2Api";
import { generalToastStyle } from "../../utils/toast.styles";

const SchemesV2Enroll = () => {
  const { schemeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [enrollmentData, setEnrollmentData] = useState({
    instalmentAmount: "",
    startDate: new Date().toISOString().split('T')[0], // Default to today's date
    customerDetails: {
      name: "",
      email: "",
      phone: "",
      address: ""
    },
    acceptTerms: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingScheme, setIsLoadingScheme] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  // const [userData, setUserData] = useState(null); // Removed unused variable

  const steps = ["Scheme Details", "Customer Information", "Confirmation"];

  // Fetch user profile data on component mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Fetch user profile data
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');
      
      if (!token || !userId) {
        console.log('No token or user ID found');
        return;
      }

      // Fetch user profile and address data in parallel
      const [profileResponse, addressResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/v1.0.0/user/userProfile.php?type=getUserProfile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/v1.0.0/user/details.php?key=address`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      ]);

      const userProfile = profileResponse.data.response.user[0];
      const addresses = addressResponse.data.response || [];
      
      // Get the first/default address
      const defaultAddress = addresses.length > 0 ? addresses[0] : null;
      
      // Format the address string
      let formattedAddress = '';
      if (defaultAddress) {
        const addressParts = [
          defaultAddress.add_line_1,
          defaultAddress.add_line_2,
          defaultAddress.city,
          defaultAddress.state,
          defaultAddress.pincode
        ].filter(part => part && part.trim() !== '');
        formattedAddress = addressParts.join(', ');
      }

      // setUserData({
      //   ...userProfile,
      //   address: formattedAddress
      // });
      
      // Pre-fill the form with user data
      // Trim phone number to last 10 digits if it's longer
      const phoneNumber = userProfile.mobile || '';
      const trimmedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
      
      setEnrollmentData(prev => ({
        ...prev,
        customerDetails: {
          name: userProfile.name || '',
          email: userProfile.email || '',
          phone: trimmedPhone,
          address: formattedAddress
        }
      }));

    } catch (error) {
      console.warn('Could not fetch user profile:', error.message);
      // Fallback to localStorage data if API fails
      const phoneNumber = localStorage.getItem('mobile') || '';
      const trimmedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
      
      const fallbackData = {
        name: localStorage.getItem('user_name') || '',
        email: localStorage.getItem('user_email') || '',
        phone: trimmedPhone,
        address: localStorage.getItem('user_address') || ''
      };
      
      if (fallbackData.name || fallbackData.email) {
        // setUserData(fallbackData);
        setEnrollmentData(prev => ({
          ...prev,
          customerDetails: fallbackData
        }));
      }
    }
  };

  useEffect(() => {
    const fetchScheme = async () => {
      if (schemeId) {
        try {
          setIsLoadingScheme(true);
          
          // First try to get the specific scheme
          try {
            const response = await schemesV2Api.getSchemeById(schemeId);
            console.log('Backend scheme response:', response);
            
            // The API returns an array of schemes, find the one with matching ID
            const schemes = response.data?.schemes || response.data?.data?.schemes || [];
            const scheme = schemes.find(s => s.id === schemeId);
            console.log('Found scheme:', scheme);
            
            if (scheme) {
              // Process the scheme data to convert string values to numbers
              const processedScheme = {
                ...scheme,
                monthlyInstalment: scheme.monthlyInstalment ? Number(scheme.monthlyInstalment) : null,
                dailyInstalment: scheme.dailyInstalment ? Number(scheme.dailyInstalment) : null,
                totalAmount: Number(scheme.totalAmount) || 0,
                minAmount: Number(scheme.minAmount) || 1000,
                maxAmount: Number(scheme.maxAmount) || 50000,
                gracePeriodsAfterDue: Number(scheme.gracePeriodsAfterDue) || 5
              };
              
              setSelectedScheme(processedScheme);
              console.log('Processed scheme data:', processedScheme);
              
              // Check for URL parameters from simulation page
              const amountFromUrl = searchParams.get('amount');
              const startDateFromUrl = searchParams.get('startDate');
              
              setEnrollmentData(prev => ({
                ...prev,
                instalmentAmount: amountFromUrl || (processedScheme.monthlyInstalment || processedScheme.dailyInstalment || 1000).toString(),
                startDate: startDateFromUrl || prev.startDate
              }));
            } else {
              throw new Error(`Scheme with ID '${schemeId}' not found`);
            }
          } catch (specificError) {
            // If specific scheme fetch fails, try to get it from all schemes
            console.warn('Specific scheme fetch failed, trying all schemes:', specificError.message);
            const allSchemesResponse = await schemesV2Api.getAllSchemes();
            const schemes = allSchemesResponse.data.schemes || allSchemesResponse.data.data?.schemes || [];
            const scheme = schemes.find(s => s.id === schemeId);
            console.log('Fallback scheme data:', scheme);
            
            if (!scheme) {
              throw new Error(`Scheme with ID '${schemeId}' not found in fallback`);
            }
            
            // Process the scheme data to convert string values to numbers
            const processedScheme = {
              ...scheme,
              monthlyInstalment: scheme.monthlyInstalment ? Number(scheme.monthlyInstalment) : null,
              dailyInstalment: scheme.dailyInstalment ? Number(scheme.dailyInstalment) : null,
              totalAmount: Number(scheme.totalAmount) || 0,
              minAmount: Number(scheme.minAmount) || 1000,
              maxAmount: Number(scheme.maxAmount) || 50000,
              gracePeriodsAfterDue: Number(scheme.gracePeriodsAfterDue) || 5
            };
            
            setSelectedScheme(processedScheme);
            
            // Check for URL parameters from simulation page
            const amountFromUrl = searchParams.get('amount');
            const startDateFromUrl = searchParams.get('startDate');
            
            setEnrollmentData(prev => ({
              ...prev,
              instalmentAmount: amountFromUrl || (processedScheme.monthlyInstalment || processedScheme.dailyInstalment || 1000).toString(),
              startDate: startDateFromUrl || prev.startDate
            }));
          }
        } catch (err) {
          toast.error(`Failed to load scheme: ${err.message}`, generalToastStyle);
        } finally {
          setIsLoadingScheme(false);
        }
      }
    };

    fetchScheme();
  }, [schemeId, searchParams]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateCurrentStep = () => {
    const errors = {};
    let isValid = true;

    switch (activeStep) {
      case 0:
        if (!enrollmentData.instalmentAmount) {
          errors.instalmentAmount = "Instalment amount is required";
          isValid = false;
        }
        if (!enrollmentData.startDate) {
          errors.startDate = "Start date is required";
          isValid = false;
        }
        
        // Validate amount against scheme limits
        if (selectedScheme && enrollmentData.instalmentAmount) {
          const amount = parseFloat(enrollmentData.instalmentAmount);
          const minAmount = selectedScheme.minAmount;
          const maxAmount = selectedScheme.maxAmount;
          
          if (minAmount && amount < minAmount) {
            errors.instalmentAmount = `Minimum instalment amount is ₹${minAmount}`;
            isValid = false;
          }
          if (maxAmount && amount > maxAmount) {
            errors.instalmentAmount = `Maximum instalment amount is ₹${maxAmount}`;
            isValid = false;
          }
        }
        break;
        
      case 1:
        const { name, email, phone } = enrollmentData.customerDetails;
        if (!name) {
          errors.name = "Name is required";
          isValid = false;
        }
        if (!email) {
          errors.email = "Email is required";
          isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
          errors.email = "Please enter a valid email address";
          isValid = false;
        }
        if (!phone) {
          errors.phone = "Phone number is required";
          isValid = false;
        } else if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
          errors.phone = "Please enter a valid 10-digit phone number";
          isValid = false;
        }
        break;
        
      case 2:
        if (!enrollmentData.acceptTerms) {
          errors.acceptTerms = "You must accept the terms and conditions";
          isValid = false;
        }
        break;
        
      default:
        // No validation needed for other steps
        break;
    }
    
    setValidationErrors(errors);
    
    if (!isValid) {
      // Show toast for general validation failure
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        toast.error(errorMessages[0], generalToastStyle);
      }
    }
    
    return isValid;
  };


  const handleEnrollment = async () => {
    // Go directly to enrollment without subscription activation
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Store the current enrollment URL to redirect back after login
        const enrollmentUrl = `/schemes-v2/enroll/${schemeId}?amount=${enrollmentData.instalmentAmount}&startDate=${enrollmentData.startDate}`;
        localStorage.setItem("redirectAfterLogin", enrollmentUrl);
        localStorage.setItem("redirect_to", enrollmentUrl);
        navigate('/signin');
        return;
      }

      const userId = localStorage.getItem('user_id');
      if (!userId) {
        toast.error("Please login to enroll in schemes", generalToastStyle);
        navigate('/signin');
        return;
      }

      const enrollmentPayload = {
        userId: parseInt(userId),
        instalmentAmount: parseFloat(enrollmentData.instalmentAmount),
        startDate: enrollmentData.startDate
      };

      // Validate enrollment data
      const validationErrors = schemesV2Utils.validateEnrollmentData(enrollmentPayload, selectedScheme);
      if (validationErrors.length > 0) {
        toast.error(validationErrors[0], generalToastStyle);
        return;
      }

      console.log('Enrolling in scheme:', schemeId, 'with payload:', enrollmentPayload);

      // Step 1: Create enrollment
      const enrollmentResponse = await schemesV2Api.enrollInScheme(schemeId, enrollmentPayload);
      const enrollment = enrollmentResponse.data;

      console.log('Enrollment response:', enrollment);
      
      // Check if enrollment was successful (API returns data directly)
      if (enrollment.enrollmentId) {
        console.log('Enrollment successful, processing first installment payment directly');
        
        // Step 2: Process first installment payment directly (skip subscription)
        await processFirstInstallmentDirectly(enrollment);
      } else {
        throw new Error('Enrollment failed - missing enrollment ID');
      }
    } catch (error) {
      console.error('Enrollment failed:', error);
      toast.error(`Enrollment failed: ${error.message}`, generalToastStyle);
    } finally {
      setIsLoading(false);
    }
  };


  // Process first installment payment directly (skip subscription)
  const processFirstInstallmentDirectly = async (enrollment) => {
    try {
      setIsLoading(true);
      console.log('Processing first installment payment directly:', enrollment);

      // Load Razorpay script first
      await schemesV2Utils.loadRazorpayScript();
      
      if (!window.Razorpay) {
        throw new Error('Razorpay SDK failed to load');
      }

      // Create Razorpay order for first installment payment
      const orderData = {
        amount: parseFloat(enrollmentData.instalmentAmount)
      };

      const orderResponse = await schemesV2Api.createInstallmentOrder(
        enrollment.enrollmentId, 
        1, 
        orderData
      );
      const order = orderResponse.data;

      console.log('First installment order created:', order);

      // Initialize Razorpay payment for first installment
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "SadāShrī Jewelkart",
        description: `First Installment Payment - ${selectedScheme.name}`,
        image: "https://sadashrijewelkart.com/logo.png",
        handler: async function (response) {
          try {
            console.log('First installment payment response:', response);
            
            // Verify first payment with backend
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              enrollment_id: enrollment.enrollmentId,
              installment_number: 1
            };

            const verificationResponse = await schemesV2Api.verifyPayment(verificationData);
            
            if (verificationResponse.success) {
              toast.success("First installment paid successfully! Your scheme is now active.", generalToastStyle);
              toast.info("You can make future payments from your schemes dashboard.", generalToastStyle);
              
              // Redirect to dashboard
              setTimeout(() => {
                navigate('/my-account/my-schemes');
              }, 2000);
            } else {
              throw new Error(verificationResponse.error?.message || 'Payment verification failed');
            }
          } catch (verificationError) {
            console.error('First payment verification failed:', verificationError);
            toast.error(`Payment verification failed: ${verificationError.message}`, generalToastStyle);
          }
        },
        theme: {
          color: "#A36E29"
        },
        modal: {
          ondismiss: function() {
            toast.info("First installment payment cancelled. You can complete it later from your schemes page.", generalToastStyle);
            navigate('/my-account/my-schemes');
          }
        },
        prefill: {
          name: enrollmentData.customerDetails?.name || localStorage.getItem('user_name') || '',
          email: enrollmentData.customerDetails?.email || localStorage.getItem('email') || '',
          contact: enrollmentData.customerDetails?.phone || localStorage.getItem('phone') || ''
        }
      };

      // Open Razorpay payment modal for first installment
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('First installment payment failed:', error);
      toast.error(`First installment payment failed: ${error.message}`, generalToastStyle);
      navigate('/my-account/my-schemes');
    } finally {
      setIsLoading(false);
    }
  };


  const handleInputChange = (field, value) => {
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Special handling for phone number - trim to last 10 digits
    if (field === 'customerDetails.phone') {
      // Remove all non-digit characters and keep only the last 10 digits
      const digitsOnly = value.replace(/\D/g, '');
      const trimmedValue = digitsOnly.slice(-10); // Keep last 10 digits
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        setEnrollmentData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: trimmedValue
          }
        }));
      } else {
        setEnrollmentData(prev => ({
          ...prev,
          [field]: trimmedValue
        }));
      }
      return;
    }
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEnrollmentData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEnrollmentData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const getSchemeColor = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return {
          primary: "#A36E29",
          secondary: "rgba(163, 110, 41, 0.1)",
          gradient: "linear-gradient(to right, #d4a76a, #A36E29)"
        };
      case "10-month-accumulation":
        return {
          primary: "#8b5a1f",
          secondary: "rgba(139, 90, 31, 0.1)",
          gradient: "linear-gradient(to right, #c18a3d, #8b5a1f)"
        };
      case "30-day-daily-savings":
        return {
          primary: "#6b4e1a",
          secondary: "rgba(107, 78, 26, 0.1)",
          gradient: "linear-gradient(to right, #c18a3d, #8b5a1f)"
        };
      default:
        return {
          primary: "#A36E29",
          secondary: "rgba(163, 110, 41, 0.1)",
          gradient: "linear-gradient(to right, #d4a76a, #A36E29)"
        };
    }
  };

  const getTermsUrl = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-11-month-direct-purchase-plan/",
          title: "11-Month Direct Purchase Plan Terms"
        };
      case "10-month-accumulation":
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
          title: "10-Month Accumulation Terms"
        };
      case "30-day-daily-savings":
        return {
          url: "https://blogs.sadashrijewelkart.com/turn-daily-savings-into-timeless-treasures/",
          title: "Daily Savings Terms"
        };
      default:
        return {
          url: "https://blogs.sadashrijewelkart.com/key-terms-10-month-accumulation/",
          title: "Terms and Conditions"
        };
    }
  };

  // Handle loading state
  if (isLoadingScheme) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 20, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ color: "#A36E29", mb: 2 }} 
          />
          <Typography variant="h6" sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
            Loading scheme details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!selectedScheme) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box sx={{ pt: 20, textAlign: "center" }}>
          <Typography variant="h5">Scheme not found</Typography>
          <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
            The scheme you're looking for doesn't exist or has been removed.
          </Typography>
          <Button 
            onClick={() => navigate("/schemes-v2")} 
            variant="contained"
            sx={{ 
              backgroundColor: "#A36E29",
              "&:hover": { backgroundColor: "#8b5a1f" }
            }}
          >
            Back to Schemes
          </Button>
        </Box>
      </Container>
    );
  }

  const colors = getSchemeColor(selectedScheme.id);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Scheme Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={selectedScheme.dailyInstalment ? "Daily Amount" : "Monthly Amount"}
                  value={enrollmentData.instalmentAmount}
                  onChange={(e) => handleInputChange('instalmentAmount', e.target.value)}
                  type="text"
                  error={!!validationErrors.instalmentAmount}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                  helperText={validationErrors.instalmentAmount || `Minimum: ₹${(selectedScheme.minAmount || 0).toLocaleString()}`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  value={enrollmentData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  type="date"
                  error={!!validationErrors.startDate}
                  helperText={validationErrors.startDate }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0] // Disable previous dates
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert 
                  severity="info"
                  sx={{
                    backgroundColor: "rgba(163, 110, 41, 0.1)",
                    borderColor: "rgba(163, 110, 41, 0.3)",
                    '& .MuiAlert-icon': {
                      color: "#A36E29",
                    },
                    '& .MuiAlert-message': {
                      color: "#A36E29",
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#A36E29" }}>
                    <strong>Scheme Summary:</strong><br />
                    • Duration: {selectedScheme.tenure}<br />
                    • Total Amount: ₹{selectedScheme.totalAmount?.toLocaleString() || "Flexible"}<br />
                    • Completion Reward: {selectedScheme.completionReward}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Customer Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={enrollmentData.customerDetails.name}
                  onChange={(e) => handleInputChange('customerDetails.name', e.target.value)}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={enrollmentData.customerDetails.email}
                  onChange={(e) => handleInputChange('customerDetails.email', e.target.value)}
                  type="email"
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={enrollmentData.customerDetails.phone}
                  onChange={(e) => handleInputChange('customerDetails.phone', e.target.value)}
                  error={!!validationErrors.phone}
                  helperText={validationErrors.phone || "Enter 10-digit mobile number (last 10 digits will be used)"}
                  required
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={enrollmentData.customerDetails.address}
                  onChange={(e) => handleInputChange('customerDetails.address', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: "#A36E29", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Ready to Enroll!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please review your enrollment details before confirming.
            </Typography>
            
            <Paper sx={{ p: 3, textAlign: "left", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Enrollment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Scheme:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedScheme.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedScheme.dailyInstalment ? 'Daily Amount:' : 'Monthly Amount:'}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ₹{enrollmentData.instalmentAmount}{selectedScheme.dailyInstalment ? '/day' : '/month'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Start Date:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {enrollmentData.startDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    UPI
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Customer:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {enrollmentData.customerDetails.name} ({enrollmentData.customerDetails.email})
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Terms and Conditions */}
            <Box sx={{ mb: 3, textAlign: "left" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enrollmentData.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                    sx={{
                      color: colors.primary,
                      '&.Mui-checked': {
                        color: colors.primary,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    I agree to the{" "}
                    <Link
                      href={getTermsUrl(selectedScheme?.id).url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: colors.primary, textDecoration: "underline" }}
                    >
                      {getTermsUrl(selectedScheme?.id).title}
                    </Link>
                    {" and understand the terms and conditions."}
                  </Typography>
                }
                sx={{ 
                  alignItems: "center",
                  marginLeft: 0,
                  marginRight: 0
                }}
              />
              {validationErrors.acceptTerms && (
                <Typography variant="body2" color="error" sx={{ mt: 1, ml: 0 }}>
                  {validationErrors.acceptTerms}
                </Typography>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 25, md: 12 },
          pb: 4
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
                          sx={{
              color: "#A36E29",
              fontWeight: 700,
              mb: 1,
              fontFamily: '"Roboto", sans-serif'
            }}
            >
              Enroll in {selectedScheme.name}
            </Typography>
            <Typography
              variant="body1"
                          sx={{
              color: "rgba(163, 110, 41, 0.8)",
              fontFamily: '"Roboto", sans-serif'
            }}
            >
              Complete the steps below to start your savings journey
            </Typography>
          </Box>

          {/* Main Card */}
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            {/* Stepper */}
            <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel={!isMobile}
                sx={{
                  '& .MuiStepLabel-root .Mui-completed': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-root .Mui-active': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-root .MuiStepLabel-iconContainer': {
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.5rem',
                    },
                  },
                  '& .MuiStepLabel-root .Mui-completed .MuiStepLabel-iconContainer': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-root .Mui-active .MuiStepLabel-iconContainer': {
                    color: '#A36E29',
                  },
                  '& .MuiStepLabel-root .MuiStepLabel-iconContainer .MuiSvgIcon-root': {
                    '&.Mui-completed': {
                      color: '#A36E29',
                    },
                    '&.Mui-active': {
                      color: '#A36E29',
                    },
                  },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {renderStepContent(activeStep)}
            </CardContent>

            {/* Navigation */}
            <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    color: "#A36E29",
                    borderColor: "#A36E29",
                    fontFamily: '"Roboto", sans-serif',
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    "&:hover": {
                      borderColor: "#A36E29",
                      backgroundColor: "rgba(163, 110, 41, 0.1)"
                    },
                    "&:disabled": {
                      color: "rgba(163, 110, 41, 0.26)",
                      borderColor: "rgba(163, 110, 41, 0.12)"
                    }
                  }}
                >
                  ← Back
                </Button>
                
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleEnrollment}
                    disabled={isLoading || !enrollmentData.acceptTerms}
                    sx={{
                      background: colors.gradient,
                      px: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "&:disabled": {
                        background: "#d4a76a",
                        color: "#fff"
                      }
                    }}
                  >
                    {isLoading && (
                      <CircularProgress 
                        size={20} 
                        sx={{ color: "white" }} 
                      />
                    )}
                    {isLoading ? "Processing..." : "Confirm Enrollment"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      background: colors.gradient,
                      px: 4
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Card>

          {/* Scheme Info Card */}
          <Card sx={{ mt: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                About {selectedScheme.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedScheme.description}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip label={`${selectedScheme.tenure} duration`} variant="outlined" />
                <Chip label={selectedScheme.completionReward} variant="outlined" />
                <Chip
                  icon={<CalendarTodayIcon />}
                  label="Flexible payments"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

    </Container>
  );
};

export default SchemesV2Enroll;
